/**
 * GSAP Animations - Swatch-Inspired Premium Animations
 * Core utilities for scroll-triggered and interactive animations
 */

(function() {
  'use strict';

  // Wait for GSAP to load
  function initGSAP() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(initGSAP, 100);
      return;
    }

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // Default easing curves (Swatch-inspired smooth transitions)
    const EASE = {
      smooth: 'power3.out',
      bounce: 'back.out(1.7)',
      elastic: 'elastic.out(1, 0.5)',
      snap: 'power4.out'
    };

    // Store for animation instances
    window.SwatchAnimations = {
      timelines: [],
      scrollTriggers: []
    };

    /**
     * Initialize all animations
     */
    function initAllAnimations() {
      // Check for reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        console.log('GSAP: Reduced motion preference detected, skipping animations');
        return;
      }

      initHeroAnimations();
      initScrollReveals();
      initProductCards();
      initParallaxEffects();
      initButtonEffects();
      initImageHovers();
      initCounterAnimations();
      initGalleryAnimations();
      initRelatedProductsAnimations();
    }

    /**
     * Hero Section Animations
     * Staggered entrance animations for hero content
     */
    function initHeroAnimations() {
      const heroes = document.querySelectorAll('.cinematic-hero, .story-hero, .collection-hero-story, [data-gsap-hero]');

      heroes.forEach(hero => {
        if (hero.dataset.gsapInit) return;
        hero.dataset.gsapInit = 'true';

        const tl = gsap.timeline({
          defaults: { ease: EASE.smooth }
        });

        // Hero badge/label
        const badge = hero.querySelector('.cinematic-hero__badge, .story-hero__badge, [data-gsap-badge]');
        if (badge) {
          tl.from(badge, { opacity: 0, y: 30, duration: 0.6 });
        }

        // Hero title
        const title = hero.querySelector('.cinematic-hero__title, .story-hero__title, .collection-hero-story__title, [data-gsap-title]');
        if (title) {
          tl.from(title, { opacity: 0, y: 50, duration: 0.8 }, '-=0.3');
        }

        // Hero subtitle/description
        const subtitle = hero.querySelector('.cinematic-hero__subtitle, .story-hero__subtitle, .collection-hero-story__subtitle, [data-gsap-subtitle]');
        if (subtitle) {
          tl.from(subtitle, { opacity: 0, y: 30, duration: 0.6 }, '-=0.4');
        }

        // Hero specs/badges
        const specs = hero.querySelectorAll('.story-hero__spec, [data-gsap-spec]');
        if (specs.length) {
          tl.from(specs, { opacity: 0, y: 20, stagger: 0.1, duration: 0.5 }, '-=0.3');
        }

        // Hero CTAs
        const ctas = hero.querySelectorAll('.cinematic-hero__cta, .story-hero__cta, .collection-hero-story__cta, [data-gsap-cta]');
        if (ctas.length) {
          tl.from(ctas, { opacity: 0, y: 20, stagger: 0.15, duration: 0.5 }, '-=0.2');
        }

        // Hero product image
        const productImage = hero.querySelector('.cinematic-hero__product-image, .story-hero__product-image, [data-gsap-product]');
        if (productImage) {
          tl.from(productImage, {
            opacity: 0,
            scale: 0.9,
            y: 40,
            duration: 1,
            ease: EASE.bounce
          }, '-=0.8');
        }

        window.SwatchAnimations.timelines.push(tl);
      });
    }

    /**
     * Scroll Reveal Animations
     * Elements animate in when scrolling into view
     */
    function initScrollReveals() {
      // Fade up reveals
      const fadeUpElements = document.querySelectorAll('.scroll-reveal, .gsap-fade-up, [data-gsap-reveal]');

      fadeUpElements.forEach(el => {
        if (el.dataset.gsapScrollInit) return;
        el.dataset.gsapScrollInit = 'true';

        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none'
          },
          opacity: 0,
          y: 60,
          duration: 0.8,
          ease: EASE.smooth
        });
      });

      // Staggered grid reveals
      const grids = document.querySelectorAll('.gsap-stagger-grid, [data-gsap-stagger]');

      grids.forEach(grid => {
        if (grid.dataset.gsapGridInit) return;
        grid.dataset.gsapGridInit = 'true';

        const items = grid.children;

        gsap.from(items, {
          scrollTrigger: {
            trigger: grid,
            start: 'top 80%',
            toggleActions: 'play none none none'
          },
          opacity: 0,
          y: 50,
          stagger: 0.1,
          duration: 0.6,
          ease: EASE.smooth
        });
      });

      // Scale reveal
      const scaleElements = document.querySelectorAll('.gsap-scale-reveal, [data-gsap-scale]');

      scaleElements.forEach(el => {
        if (el.dataset.gsapScaleInit) return;
        el.dataset.gsapScaleInit = 'true';

        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none'
          },
          opacity: 0,
          scale: 0.8,
          duration: 0.8,
          ease: EASE.bounce
        });
      });

      // Slide in from sides
      const slideLeftElements = document.querySelectorAll('.gsap-slide-left, [data-gsap-slide="left"]');
      slideLeftElements.forEach(el => {
        if (el.dataset.gsapSlideInit) return;
        el.dataset.gsapSlideInit = 'true';

        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none'
          },
          opacity: 0,
          x: -80,
          duration: 0.8,
          ease: EASE.smooth
        });
      });

      const slideRightElements = document.querySelectorAll('.gsap-slide-right, [data-gsap-slide="right"]');
      slideRightElements.forEach(el => {
        if (el.dataset.gsapSlideInit) return;
        el.dataset.gsapSlideInit = 'true';

        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 85%',
            toggleActions: 'play none none none'
          },
          opacity: 0,
          x: 80,
          duration: 0.8,
          ease: EASE.smooth
        });
      });
    }

    /**
     * Product Card Animations
     * Hover effects and entrance animations for product cards
     */
    function initProductCards() {
      const cards = document.querySelectorAll('.product-card-premium, .story-product-card, [data-gsap-card]');

      cards.forEach(card => {
        if (card.dataset.gsapCardInit) return;
        card.dataset.gsapCardInit = 'true';

        const image = card.querySelector('img');
        const quickAdd = card.querySelector('.product-card-premium__quick-add');

        // Hover animation
        card.addEventListener('mouseenter', () => {
          gsap.to(card, {
            y: -10,
            boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
            duration: 0.4,
            ease: EASE.smooth
          });

          if (image) {
            gsap.to(image, {
              scale: 1.08,
              duration: 0.6,
              ease: EASE.smooth
            });
          }

          if (quickAdd) {
            gsap.to(quickAdd, {
              opacity: 1,
              y: 0,
              duration: 0.3,
              ease: EASE.smooth
            });
          }
        });

        card.addEventListener('mouseleave', () => {
          gsap.to(card, {
            y: 0,
            boxShadow: '0 15px 40px rgba(0,0,0,0.08)',
            duration: 0.4,
            ease: EASE.smooth
          });

          if (image) {
            gsap.to(image, {
              scale: 1,
              duration: 0.6,
              ease: EASE.smooth
            });
          }

          if (quickAdd) {
            gsap.to(quickAdd, {
              opacity: 0,
              y: 20,
              duration: 0.3,
              ease: EASE.smooth
            });
          }
        });
      });
    }

    /**
     * Parallax Effects
     * Subtle parallax scrolling for images and backgrounds
     */
    function initParallaxEffects() {
      const parallaxElements = document.querySelectorAll('.gsap-parallax, [data-gsap-parallax]');

      parallaxElements.forEach(el => {
        if (el.dataset.gsapParallaxInit) return;
        el.dataset.gsapParallaxInit = 'true';

        const speed = parseFloat(el.dataset.gsapParallaxSpeed) || 0.3;

        gsap.to(el, {
          scrollTrigger: {
            trigger: el.closest('section') || el,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1
          },
          y: () => window.innerHeight * speed,
          ease: 'none'
        });
      });

      // Hero product parallax on mouse move
      const heroProducts = document.querySelectorAll('.cinematic-hero__product-image, [data-gsap-mouse-parallax]');

      heroProducts.forEach(product => {
        if (product.dataset.gsapMouseInit) return;
        product.dataset.gsapMouseInit = 'true';

        const parent = product.closest('.cinematic-hero, .story-hero') || product.parentElement;

        parent.addEventListener('mousemove', (e) => {
          const rect = parent.getBoundingClientRect();
          const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
          const y = (e.clientY - rect.top - rect.height / 2) / rect.height;

          gsap.to(product, {
            x: x * 30,
            y: y * 20,
            rotateY: x * 5,
            rotateX: -y * 5,
            duration: 0.5,
            ease: EASE.smooth
          });
        });

        parent.addEventListener('mouseleave', () => {
          gsap.to(product, {
            x: 0,
            y: 0,
            rotateY: 0,
            rotateX: 0,
            duration: 0.8,
            ease: EASE.elastic
          });
        });
      });
    }

    /**
     * Button Effects
     * Hover animations for buttons
     */
    function initButtonEffects() {
      const buttons = document.querySelectorAll('.premium-btn, .story-hero__cta, .story-block__cta, [data-gsap-btn]');

      buttons.forEach(btn => {
        if (btn.dataset.gsapBtnInit) return;
        btn.dataset.gsapBtnInit = 'true';

        // Arrow animation on hover
        const arrow = btn.querySelector('svg, .arrow, [data-gsap-arrow]');

        btn.addEventListener('mouseenter', () => {
          gsap.to(btn, {
            scale: 1.02,
            duration: 0.2,
            ease: EASE.smooth
          });

          if (arrow) {
            gsap.to(arrow, {
              x: 5,
              duration: 0.3,
              ease: EASE.smooth
            });
          }
        });

        btn.addEventListener('mouseleave', () => {
          gsap.to(btn, {
            scale: 1,
            duration: 0.2,
            ease: EASE.smooth
          });

          if (arrow) {
            gsap.to(arrow, {
              x: 0,
              duration: 0.3,
              ease: EASE.smooth
            });
          }
        });
      });
    }

    /**
     * Image Hover Effects
     * Scale and reveal effects on image hover
     */
    function initImageHovers() {
      const imageContainers = document.querySelectorAll('.story-block__media, .collection-showcase__preview-media, [data-gsap-image-hover]');

      imageContainers.forEach(container => {
        if (container.dataset.gsapImageInit) return;
        container.dataset.gsapImageInit = 'true';

        const image = container.querySelector('img');
        if (!image) return;

        container.addEventListener('mouseenter', () => {
          gsap.to(image, {
            scale: 1.05,
            duration: 0.8,
            ease: EASE.smooth
          });
        });

        container.addEventListener('mouseleave', () => {
          gsap.to(image, {
            scale: 1,
            duration: 0.8,
            ease: EASE.smooth
          });
        });
      });
    }

    /**
     * Counter Animations
     * Animated number counting for statistics
     */
    function initCounterAnimations() {
      const counters = document.querySelectorAll('.gsap-counter, [data-gsap-counter]');

      counters.forEach(counter => {
        if (counter.dataset.gsapCounterInit) return;
        counter.dataset.gsapCounterInit = 'true';

        const target = parseFloat(counter.dataset.gsapCounterTarget) || parseFloat(counter.textContent) || 0;
        const duration = parseFloat(counter.dataset.gsapCounterDuration) || 2;
        const decimals = parseInt(counter.dataset.gsapCounterDecimals) || 0;

        const obj = { value: 0 };

        ScrollTrigger.create({
          trigger: counter,
          start: 'top 85%',
          onEnter: () => {
            gsap.to(obj, {
              value: target,
              duration: duration,
              ease: EASE.smooth,
              onUpdate: () => {
                counter.textContent = obj.value.toFixed(decimals);
              }
            });
          },
          once: true
        });
      });
    }

    /**
     * Gallery Mosaic Animations
     * Scroll-triggered entrance for the final gallery section and enhanced tile hovers
     */
    function initGalleryAnimations() {
      // --- a) Header reveal ---
      const galleryHead = document.querySelector('.story-description__final-gallery-head');

      if (galleryHead && !galleryHead.dataset.gsapGalleryHeadInit) {
        galleryHead.dataset.gsapGalleryHeadInit = 'true';

        gsap.fromTo(galleryHead,
          { opacity: 0, y: 40 },
          {
            scrollTrigger: {
              trigger: galleryHead,
              start: 'top 80%',
              toggleActions: 'play none none none'
            },
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: EASE.smooth
          }
        );
      }

      // --- b) Mosaic tiles staggered reveal ---
      const mosaic = document.querySelector('.story-description__mosaic');

      if (mosaic && !mosaic.dataset.gsapMosaicInit) {
        mosaic.dataset.gsapMosaicInit = 'true';

        const tiles = mosaic.querySelectorAll('.story-description__mosaic-item');

        if (tiles.length) {
          gsap.fromTo(tiles,
            { opacity: 0, scale: 0.85, y: 40 },
            {
              scrollTrigger: {
                trigger: mosaic,
                start: 'top 80%',
                toggleActions: 'play none none none'
              },
              opacity: 1,
              scale: 1,
              y: 0,
              stagger: 0.12,
              duration: 0.7,
              ease: EASE.bounce
            }
          );

          // --- c) Enhanced GSAP hover per tile ---
          tiles.forEach(tile => {
            if (tile.dataset.gsapTileHoverInit) return;
            tile.dataset.gsapTileHoverInit = 'true';

            const figure = tile.querySelector('figure') || tile;
            const image = tile.querySelector('img');

            tile.addEventListener('mouseenter', () => {
              gsap.to(figure, {
                boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
                duration: 0.5,
                ease: EASE.smooth
              });

              if (image) {
                gsap.to(image, {
                  scale: 1.06,
                  duration: 0.5,
                  ease: EASE.smooth
                });
              }
            });

            tile.addEventListener('mouseleave', () => {
              gsap.to(figure, {
                boxShadow: 'none',
                duration: 0.5,
                ease: EASE.smooth
              });

              if (image) {
                gsap.to(image, {
                  scale: 1,
                  duration: 0.5,
                  ease: EASE.smooth
                });
              }
            });
          });
        }
      }
    }

    /**
     * Related Products Section Animations
     * Scroll-triggered entrance for the story carousel section and card hover effects
     */
    function initRelatedProductsAnimations() {
      // --- a) Section title reveal ---
      const carousel = document.querySelector('.story-carousel');
      const carouselTitle = carousel ? carousel.querySelector('.story-carousel__title') : null;

      if (carousel && carouselTitle && !carouselTitle.dataset.gsapCarouselTitleInit) {
        carouselTitle.dataset.gsapCarouselTitleInit = 'true';

        gsap.fromTo(carouselTitle,
          { opacity: 0, y: 30 },
          {
            scrollTrigger: {
              trigger: carousel,
              start: 'top 85%',
              toggleActions: 'play none none none'
            },
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: EASE.smooth
          }
        );
      }

      // --- b) Product cards staggered entrance ---
      const carouselGrid = document.querySelector('.story-carousel__grid');

      if (carouselGrid && !carouselGrid.dataset.gsapCarouselGridInit) {
        carouselGrid.dataset.gsapCarouselGridInit = 'true';

        const items = carouselGrid.querySelectorAll('.story-carousel__item');

        if (items.length) {
          gsap.fromTo(items,
            { opacity: 0, y: 50, scale: 0.92 },
            {
              scrollTrigger: {
                trigger: carouselGrid,
                start: 'top 80%',
                toggleActions: 'play none none none'
              },
              opacity: 1,
              y: 0,
              scale: 1,
              stagger: 0.1,
              duration: 0.6,
              ease: EASE.smooth
            }
          );

          // --- c) Card hover interactions ---
          items.forEach(item => {
            if (item.dataset.gsapItemHoverInit) return;
            item.dataset.gsapItemHoverInit = 'true';

            const image = item.querySelector('img');

            item.addEventListener('mouseenter', () => {
              gsap.to(item, {
                y: -8,
                boxShadow: '0 20px 50px rgba(0,0,0,0.12)',
                duration: 0.4,
                ease: EASE.smooth
              });

              if (image) {
                gsap.to(image, {
                  scale: 1.08,
                  duration: 0.5,
                  ease: EASE.smooth
                });
              }
            });

            item.addEventListener('mouseleave', () => {
              gsap.to(item, {
                y: 0,
                boxShadow: 'none',
                duration: 0.4,
                ease: EASE.smooth
              });

              if (image) {
                gsap.to(image, {
                  scale: 1,
                  duration: 0.5,
                  ease: EASE.smooth
                });
              }
            });
          });
        }
      }
    }

    /**
     * Text Split Animation
     * Character-by-character reveal
     */
    function animateTextSplit(element) {
      if (!element || element.dataset.gsapSplitInit) return;
      element.dataset.gsapSplitInit = 'true';

      const text = element.textContent;
      element.innerHTML = '';

      text.split('').forEach((char, i) => {
        const span = document.createElement('span');
        span.textContent = char === ' ' ? '\u00A0' : char;
        span.style.display = 'inline-block';
        span.style.opacity = '0';
        element.appendChild(span);
      });

      gsap.to(element.children, {
        scrollTrigger: {
          trigger: element,
          start: 'top 85%',
          toggleActions: 'play none none none'
        },
        opacity: 1,
        y: 0,
        stagger: 0.02,
        duration: 0.4,
        ease: EASE.smooth,
        from: { opacity: 0, y: 20 }
      });
    }

    /**
     * Horizontal Scroll Carousel
     * Smooth horizontal scrolling for carousels
     */
    window.initGSAPCarousel = function(container) {
      if (!container || container.dataset.gsapCarouselInit) return;
      container.dataset.gsapCarouselInit = 'true';

      const track = container.querySelector('.carousel-track, [data-gsap-track]');
      if (!track) return;

      const items = track.children;
      const totalWidth = Array.from(items).reduce((acc, item) => acc + item.offsetWidth + 20, 0);
      const maxScroll = totalWidth - container.offsetWidth;

      let currentX = 0;
      let targetX = 0;
      let isDragging = false;
      let startX = 0;
      let startScrollX = 0;

      // Smooth scroll loop
      function updateScroll() {
        currentX += (targetX - currentX) * 0.1;
        gsap.set(track, { x: -currentX });
        requestAnimationFrame(updateScroll);
      }
      updateScroll();

      // Drag interaction
      container.addEventListener('mousedown', (e) => {
        isDragging = true;
        startX = e.clientX;
        startScrollX = targetX;
        container.style.cursor = 'grabbing';
      });

      window.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        const diff = startX - e.clientX;
        targetX = Math.max(0, Math.min(maxScroll, startScrollX + diff));
      });

      window.addEventListener('mouseup', () => {
        isDragging = false;
        container.style.cursor = 'grab';
      });

      // Arrow navigation
      const prevBtn = container.querySelector('[data-gsap-prev]');
      const nextBtn = container.querySelector('[data-gsap-next]');
      const scrollAmount = container.offsetWidth * 0.8;

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          targetX = Math.max(0, targetX - scrollAmount);
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          targetX = Math.min(maxScroll, targetX + scrollAmount);
        });
      }

      container.style.cursor = 'grab';
    };

    /**
     * Tab Animation
     * Smooth content switching for tabs
     */
    window.animateTabSwitch = function(oldContent, newContent, direction = 1) {
      const tl = gsap.timeline();

      tl.to(oldContent, {
        opacity: 0,
        x: -30 * direction,
        duration: 0.3,
        ease: EASE.smooth,
        onComplete: () => {
          oldContent.style.display = 'none';
        }
      });

      tl.set(newContent, { display: 'block', opacity: 0, x: 30 * direction });

      tl.to(newContent, {
        opacity: 1,
        x: 0,
        duration: 0.4,
        ease: EASE.smooth
      });

      return tl;
    };

    /**
     * Refresh ScrollTrigger
     * Call after dynamic content changes
     */
    window.refreshGSAP = function() {
      ScrollTrigger.refresh();
    };

    /**
     * Kill all ScrollTriggers in a container
     * Call when removing sections
     */
    window.killGSAPInContainer = function(container) {
      ScrollTrigger.getAll().forEach(st => {
        if (container.contains(st.trigger)) {
          st.kill();
        }
      });
    };

    // Initialize all animations
    initAllAnimations();

    // Shopify Theme Editor Integration
    document.addEventListener('shopify:section:load', function(event) {
      ScrollTrigger.refresh();
      initAllAnimations();
    });

    document.addEventListener('shopify:section:unload', function(event) {
      window.killGSAPInContainer(event.target);
    });

    document.addEventListener('shopify:section:select', function(event) {
      ScrollTrigger.refresh();
    });

    // Re-initialize on Flickity/carousel updates
    document.addEventListener('flickity-ready', function() {
      ScrollTrigger.refresh();
    });

    console.log('GSAP Animations initialized successfully');
  }

  // Start initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGSAP);
  } else {
    initGSAP();
  }

})();
